import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useT } from "../lib/i18n.ts";
import { supabase } from "../lib/supabase.ts";

export const RETURN_TO_KEY = "instead.signin.returnTo";

type Details = { authorization_id: string; client: { name: string; uri?: string; logo_uri?: string }; scope: string; user: { email: string } };

/**
 * The consent step of Supabase's OAuth 2.1 server (D-23/D-26): an AI app
 * ("Connect to Claude") lands here with ?authorization_id=…. Signed out →
 * sign in first and come back. Signed in → one question, Allow or Deny,
 * then straight back to the AI app. Allowing IS the consent (D-26).
 */
export function OAuthConsent() {
  const t = useT();
  const nav = useNavigate();
  const id = new URLSearchParams(window.location.search).get("authorization_id");
  const [details, setDetails] = useState<Details | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supabase || !id) { setError(t("consent.missing")); return; }
    let active = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        try { localStorage.setItem(RETURN_TO_KEY, window.location.pathname + window.location.search); } catch { /* fine */ }
        await nav({ to: "/signin" });
        return;
      }
      const res = await supabase.auth.oauth.getAuthorizationDetails(id);
      if (!active) return;
      if (res.error) { setError(res.error.message); return; }
      if ("redirect_url" in res.data) { window.location.assign(res.data.redirect_url); return; }
      setDetails(res.data as Details);
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function decide(allow: boolean) {
    if (!supabase || !id) return;
    setBusy(true);
    const res = allow ? await supabase.auth.oauth.approveAuthorization(id) : await supabase.auth.oauth.denyAuthorization(id);
    setBusy(false);
    if (res.error) { setError(res.error.message); return; }
    window.location.assign(res.data.redirect_url);
  }

  return (
    <main className="shell-main" style={{ display: "flex", flexDirection: "column", justifyContent: "center", minHeight: "100vh", gap: "var(--sp-5)" }}>
      <div className="signin-brand">
        <div className="display" style={{ color: "var(--accent)" }}>instead</div>
      </div>
      {details && (
        <div className="card" style={{ padding: "var(--sp-5)", display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
          <h1 className="title">{t("consent.title", { app: details.client.name })}</h1>
          <p className="meta">{t("consent.body")}</p>
          <p className="micro">{details.user.email}</p>
          <div className="dialog-actions">
            <button className="btn" type="button" disabled={busy} onClick={() => decide(false)}>{t("consent.deny")}</button>
            <button className="btn btn-primary" type="button" disabled={busy} onClick={() => decide(true)}>{t("consent.allow")}</button>
          </div>
        </div>
      )}
      {error && <p role="alert" className="meta" style={{ color: "var(--danger)", textAlign: "center" }}>{error}</p>}
    </main>
  );
}
